import React, { useState, useEffect } from 'react';
import { IPRange, ipRangeApi, Office } from '../services/api';
import IPRangeForm from './IPRangeForm';
import StaticIPList from './StaticIPList';
import DhcpIPList from './DhcpIPList';
import ConfirmDialog from './ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';
import './IPRangeList.css';

interface IPRangeListProps {
  office: Office | null;
  searchQuery?: string;
  selectedRangeId?: number | null;
  onRangeSelect?: (rangeId: number | null) => void;
}

const IPRangeList: React.FC<IPRangeListProps> = ({ office, searchQuery = '', selectedRangeId, onRangeSelect }) => {
  const { canEdit } = useAuth();
  const [ipRanges, setIpRanges] = useState<IPRange[]>([]);
  const [selectedRange, setSelectedRange] = useState<IPRange | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingRange, setEditingRange] = useState<IPRange | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; range: IPRange | null }>({
    isOpen: false,
    range: null
  });

  useEffect(() => {
    if (office) {
      loadIPRanges();
      // Only clear selected range if there's no selectedRangeId
      if (!selectedRangeId) {
        setSelectedRange(null);
        if (onRangeSelect) {
          onRangeSelect(null);
        }
      }
    } else {
      setIpRanges([]);
      setSelectedRange(null);
    }
  }, [office]);

  // Auto-select range when selectedRangeId changes or ipRanges are loaded
  useEffect(() => {
    if (selectedRangeId) {
      if (ipRanges.length > 0) {
        const range = ipRanges.find(r => r.id === selectedRangeId);
        if (range) {
          setSelectedRange(range);
          if (onRangeSelect) {
            onRangeSelect(range.id);
          }
        }
      }
      // If ipRanges not loaded yet, the range will be selected once they load
    } else if (!selectedRangeId && selectedRange) {
      // Clear selection if selectedRangeId is cleared
      setSelectedRange(null);
    }
  }, [selectedRangeId, ipRanges]);

  const loadIPRanges = async () => {
    if (!office) return;
    
    try {
      setLoading(true);
      const response = await ipRangeApi.getAll(office.id);
      setIpRanges(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load IP ranges');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRange = () => {
    if (!office) {
      alert('Please select an office first');
      return;
    }
    setEditingRange(null);
    setShowForm(true);
  };

  const handleEditRange = (range: IPRange) => {
    setEditingRange(range);
    setShowForm(true);
  };

  const handleDeleteRange = (range: IPRange) => {
    setDeleteConfirm({ isOpen: true, range });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.range) return;
    
    try {
      await ipRangeApi.delete(deleteConfirm.range.id);
      await loadIPRanges();
      if (selectedRange?.id === deleteConfirm.range.id) {
        setSelectedRange(null);
      }
      setDeleteConfirm({ isOpen: false, range: null });
    } catch (err) {
      setError('Failed to delete IP range');
      console.error(err);
      setDeleteConfirm({ isOpen: false, range: null });
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingRange(null);
  };

  const handleFormSubmit = async () => {
    await loadIPRanges();
    handleFormClose();
  };

  const calculateIPUsage = (range: IPRange): string => {
    const activeIPs = range.static_ips.filter(ip => ip.is_active).length;
    const totalStatic = calculateTotalIPs(range.static_start, range.static_end);
    return `${activeIPs}/${totalStatic}`;
  };

  const calculateTotalIPs = (start?: string, end?: string): number => {
    if (!start || !end) return 0;
    const startNum = ipToNumber(start);
    const endNum = ipToNumber(end);
    return endNum - startNum + 1;
  };

  const ipToNumber = (ip: string): number => {
    const parts = ip.split('.').map(Number);
    return (parts[0] << 24) + (parts[1] << 16) + (parts[2] << 8) + parts[3];
  };

  if (!office) {
    return (
      <div className="ip-range-list">
        <div className="placeholder">Select an office to view IP ranges</div>
      </div>
    );
  }

  return (
    <div className="ip-range-container">
      <div className="ip-range-list">
        <div className="ip-range-header">
          <h2>IP Ranges - {office.building} {office.floor}</h2>
          {canEdit() && (
            <button className="btn btn-primary" onClick={handleCreateRange}>
              Add IP Range
            </button>
          )}
        </div>

        {loading && <div className="loading">Loading IP ranges...</div>}
        {error && <div className="error">{error}</div>}

        <div className="ip-range-grid">
          {ipRanges.map((range) => (
            <div
              key={range.id}
              className={`ip-range-card ${selectedRange?.id === range.id ? 'selected' : ''}`}
              onClick={() => setSelectedRange(range)}
            >
              <div className="ip-range-card-header">
                <h3>{range.network}/{range.subnet_mask}</h3>
                {canEdit() && (
                  <div className="ip-range-actions">
                    <button
                      className="btn btn-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditRange(range);
                      }}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteRange(range);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
              
              <div className="ip-range-details">
                {range.vlan_id && <p>VLAN: {range.vlan_id}</p>}
                {range.dhcp_start && range.dhcp_end && (
                  <p>DHCP: {range.dhcp_start} - {range.dhcp_end}</p>
                )}
                {range.static_start && range.static_end && (
                  <p>Static: {range.static_start} - {range.static_end}</p>
                )}
                <p>Usage: {calculateIPUsage(range)} IPs assigned</p>
                {range.description && (
                  <p className="ip-range-description">{range.description}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {showForm && (
          <IPRangeForm
            officeId={office.id}
            ipRange={editingRange}
            onClose={handleFormClose}
            onSubmit={handleFormSubmit}
          />
        )}
      </div>

      {selectedRange && (
        <div className="static-ip-section">
          <StaticIPList ipRange={selectedRange} onUpdate={loadIPRanges} searchQuery={searchQuery} />
          <DhcpIPList ipRange={selectedRange} searchQuery={searchQuery} />
        </div>
      )}

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Delete IP Range"
        message={`Are you sure you want to delete IP range "${deleteConfirm.range?.network}/${deleteConfirm.range?.subnet_mask}"?\n\nThis will also delete all associated static IP assignments.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, range: null })}
        variant="danger"
      />
    </div>
  );
};

export default IPRangeList;